import { Vector3 } from 'three';
import type * as zod from 'zod';

import type { CartesianPose, Plane } from '@sb/geometry';
import type { Camera } from '@sb/integrations/implementations/Camera/implementation/Camera';
import type { CameraConfiguration } from '@sb/integrations/implementations/Camera/types';
import type { WristCamera } from '@sb/integrations/implementations/WristCamera/implementation/WristCamera';
import type {
  CameraIntrinsicsExtended,
  ExtrinsicsKinematics,
} from '@sb/integrations/types/cameraTypes';
import * as log from '@sb/log';
import type { ArmJointPositions } from '@sb/motion-planning';
import { FailureKind } from '@sb/routine-runner/FailureKind';
import type { Space } from '@sb/routine-runner/types';
import { resolveLocateGpuTaskRunner } from '@sb/routine-runner/vision/helpers';
import type { LocateResult } from '@sb/routine-runner/vision/LocateTypes';
import {
  runLocate,
  captureDepthWithPose,
} from '@sb/routine-runner/vision/runLocate';
import { getFeatureFlagSync } from '@sb/service-interfaces/host/featureFlags';
import { six } from '@sb/utilities';

import { captureFrameToStorage } from '../Capture/captureFrameToStorage';
import type { ImageValidationFailure } from '../Capture/captureUtils';
import { nameFrame, readFileNameParts } from '../Capture/fileNameParts';
import { makeCaptureStorageNotifier } from '../Capture/notifyCaptureStorage';
import type { StepPlayArguments } from '../Step';
import Step from '../Step';

import Arguments, { projectionMethodNeedsDepth } from './Arguments';
import Variables from './Variables';

const ns = log.namespace('locateStep');

type Arguments = zod.infer<typeof Arguments>;

type Variables = zod.infer<typeof Variables>;

/**
 * We save a dummy set of joint angles for each position. Calculation of the joint angles
 * to use to reach the position should be deferred until a move arm step is played
 */
const NULL_JOINT_ANGLES: ArmJointPositions = six(0);

export default class LocateStep extends Step<Arguments, Variables> {
  public static areSubstepsRequired = false;

  public static Arguments = Arguments;

  public static Variables = Variables;

  public initializeVariableState(): void {
    this.variables = {
      latestResult: null,
      resultCount: 0,
      latestSaveFilepath: null,
      captureTimestamp: null,
      fullResults: null,
    };
  }

  private getPlaneSpaceItem(): Space.Plane {
    if (!this.args.planeID) {
      throw new Error('No planeID provided');
    }

    const plane = this.routineFrame.getSpaceItem(this.args.planeID);

    if (plane.kind !== 'plane') {
      throw new Error(`SpaceItem ${this.args.planeID} is not a plane`);
    }

    return plane;
  }

  private getPlaneFromSpaceItemID(): Plane {
    const plane = this.getPlaneSpaceItem();

    const planePositions = plane.positions.map(
      (position) =>
        new Vector3(position.pose.x, position.pose.y, position.pose.z),
    );

    return {
      origin: planePositions[0],
      plusX: planePositions[1],
      plusY: planePositions[2],
    };
  }

  /**
   * If `captureConfig` is set, saves the locate image to storage and sets
   * capture-related variables.
   *
   * Returns an `ImageValidationFailure` when the image fails validation
   * (caller should pass it to `fail`), or `null` on success / when capture
   * is not applicable.
   */
  private async maybeCaptureImage(
    image: ArrayBuffer,
    locateResults?: LocateResult[],
  ): Promise<ImageValidationFailure | null> {
    const { captureConfig } = this.args;

    if (!captureConfig) {
      return null;
    }

    const { captureConfig: _captureConfig, ...stepParameters } = this.args;

    const { frameName } = nameFrame({
      stepKind: 'Locate',
      fileNameParts: readFileNameParts(captureConfig.fileName, this),
    });

    return captureFrameToStorage({
      image,
      captureConfig,
      stepKind: 'Locate',
      stepParameters,
      robotId: this.routineContext.robotId,
      routineId: this.routineFrame.routineId,
      ...frameName,
      reportStoragePressure: makeCaptureStorageNotifier(this.routineContext),
      results: { locateResults },
      evaluateExpression: (expression) =>
        this.routineFrame.evaluateExpression(expression),
      getDeviceById: (id) => this.routineContext.equipment.getDeviceById(id),
      publishVariable: (name, value) => this.setVariable(name, value),
    });
  }

  public async _play({ fail }: StepPlayArguments): Promise<void> {
    try {
      log.info(ns`locate.play`, 'Playing Locate step');

      const useCameraBotCpp = getFeatureFlagSync('useCppCameraBot');

      let cameraEquipment: Camera | WristCamera | undefined;
      let accuracyCalibration: Space.AccuracyCalibrationEntry[] | null = null;
      let intrinsics: CameraIntrinsicsExtended | undefined;
      let extrinsics: ExtrinsicsKinematics | undefined;

      if (useCameraBotCpp) {
        // camera-bot-cpp is enabled, use camera equipment ID
        if (!this.args.cameraEquipmentId) {
          return fail({
            failure: {
              kind: FailureKind.CameraFailure,
            },
            failureReason: 'No camera equipment ID provided',
          });
        }

        // Use new Camera implementation from camera-bot
        // If cameraEquipmentId is set, use that specific camera
        cameraEquipment = this.routineContext.equipment.getDeviceById(
          this.args.cameraEquipmentId,
        ) as Camera | undefined;

        if (!cameraEquipment) {
          return fail({
            failure: {
              kind: FailureKind.CameraFailure,
            },
            failureReason: `Camera equipment with id ${this.args.cameraEquipmentId} not found`,
          });
        }

        const isManualPlane =
          (this.args.projectionMethod ?? 'ManualPlane') === 'ManualPlane';

        const cameraConfig = cameraEquipment.getConfig() as CameraConfiguration;
        const { extrinsicsId } = cameraConfig;
        const { intrinsicsId } = cameraConfig;

        if (extrinsicsId == null || intrinsicsId == null) {
          return fail({
            failure: {
              kind: FailureKind.CameraFailure,
            },
            failureReason: `Camera equipment ${this.args.cameraEquipmentId} has no extrinsics or intrinsics set.`,
          });
        }

        extrinsics = cameraEquipment.getExtrinsics(true);

        intrinsics =
          cameraConfig.intrinsicsData[intrinsicsId].intrinsics ?? undefined;

        if (extrinsics == null) {
          return fail({
            failure: {
              kind: FailureKind.CameraFailure,
            },
            failureReason: `Camera equipment ${this.args.cameraEquipmentId} has no extrinsics set.`,
          });
        }

        if (intrinsics == null) {
          return fail({
            failure: {
              kind: FailureKind.CameraFailure,
            },
            failureReason: `Camera equipment ${this.args.cameraEquipmentId} has no intrinsics set.`,
          });
        }

        if (isManualPlane) {
          const plane = this.getPlaneSpaceItem();

          const accuracyCalibrationFull = Object.values(
            plane.cameraAccuracyCalibrations,
          ).find(
            (calibration) =>
              calibration.equipmentId === this.args.cameraEquipmentId &&
              calibration.extrinsicsId === extrinsicsId,
          );

          if (accuracyCalibrationFull != null) {
            accuracyCalibration = accuracyCalibrationFull.calibrationEntries;
          } else {
            log.info(
              ns`locate.play`,
              "No accuracy calibration found for the selected camera's active extrinsics.",
              {
                cameraEquipmentId: this.args.cameraEquipmentId,
                extrinsicsId,
              },
            );
          }
        }
      } else {
        // camera-bot-cpp is not enabled, use wrist camera
        cameraEquipment =
          this.routineContext.equipment.getWristCamera() ?? undefined;

        if (!cameraEquipment) {
          return fail({
            failure: {
              kind: FailureKind.CameraFailure,
            },
            failureReason: 'No wrist camera found.',
          });
        }

        if ((this.args.projectionMethod ?? 'ManualPlane') === 'ManualPlane') {
          accuracyCalibration = this.getPlaneSpaceItem().calibrationEntries;
        }
      }

      const projectionMethod = this.args.projectionMethod ?? 'ManualPlane';

      if (!useCameraBotCpp && projectionMethod !== 'ManualPlane') {
        return fail({
          failure: {
            kind: FailureKind.CameraFailure,
          },
          failureReason: `The ${projectionMethod} projection method requires camera-bot-cpp. Enable the useCppCameraBot feature flag.`,
        });
      }

      // Only ManualPlane projects onto taught geometry; the rest measure it.
      const plane =
        projectionMethod === 'ManualPlane'
          ? this.getPlaneFromSpaceItemID()
          : undefined;

      let depthImage: ArrayBuffer | undefined;
      // Only set on the depth paths. Where no depth frame is fetched there is
      // nothing for the caller's pose to match, and runLocate's own sample —
      // taken immediately before its colour-frame request — is closer.
      let wristPoseAtCapture: CartesianPose | undefined;

      // Every projection method but ManualPlane reads depth, as does the
      // Detection3D detection *method* (which runs its own 2D pass first).
      const needsDepth =
        projectionMethodNeedsDepth(projectionMethod) ||
        this.args.method.kind === 'Detection3D';

      if (needsDepth) {
        try {
          ({ depthImage, wristPoseAtCapture } = await captureDepthWithPose(
            cameraEquipment,
            () => this.routineContext.getRoutineRunnerState(),
          ));
        } catch (err) {
          return fail({
            failure: {
              kind: FailureKind.CameraFailure,
            },
            failureReason: `Failed to capture depth frame: ${err instanceof Error ? err.message : String(err)}`,
          });
        }
      }

      // Honor the method's own GPU selection ("" = "None (CPU only)") for
      // every method kind — do not fall back to the first healthy GPU box the
      // user never selected (it may run a different software version than the
      // local vision-bot, and plane projection must run where the user chose).
      const gpuTaskRunner = resolveLocateGpuTaskRunner(
        this.routineContext.equipment,
        this.args.method,
        'locate.gpuUnhealthy',
      );

      const { results: runLocateResults, image } = await runLocate({
        camera: {
          equipment: cameraEquipment,
          integration: this.args.camera,
        },
        wristPoseAtCapture,
        regionOfInterest: this.args.regionOfInterest,
        method: this.args.method,
        plane,
        objectHeight: this.args.objectHeight,
        resultsLimit: this.args.filters.resultsLimit,
        transform: this.args.transform,
        accuracyCalibration,
        vision: this.routineContext.vision,
        getState: () => this.routineContext.getRoutineRunnerState(),
        equipment: this.routineContext.equipment,
        intrinsics,
        extrinsics,
        gpuTaskRunner,
        projectionMethod,
        ransacParams: this.args.ransacParams,
        resultOrdering: this.args.resultOrdering,
        depthImage,
        maxTiltAngle: this.args.maxTiltAngle,
        minInlierRatio: this.args.minInlierRatio,
        fallbackToTopDown: this.args.fallbackToTopDown,
        detection3dConfig: this.args.detection3dConfig,
        disablePlaneOrientation: this.args.disablePlaneOrientation,
      });

      const positions: Space.Position[] = runLocateResults.map((result) => {
        return {
          pose: result.pose,
          jointAngles: NULL_JOINT_ANGLES,
          tcpOption: 'ee-any',
        };
      });

      this.setVariable('latestResult', positions);
      this.setVariable('resultCount', positions.length);
      this.setVariable('fullResults', runLocateResults);

      this.routineFrame.setPositionListEntries(
        this.args.positionListID,
        positions,
      );

      // Optionally save the captured image (feature-flag gated)
      const captureFailure = await this.maybeCaptureImage(
        image,
        runLocateResults,
      );

      if (captureFailure) {
        return fail(captureFailure);
      }
    } catch (error) {
      log.error(ns`locate.play`, 'Locate failed', error);

      if ((error as any).failureKind === FailureKind.CameraFailure) {
        return fail({
          failure: {
            kind: FailureKind.CameraFailure,
          },
          failureReason: error.message,
          error,
        });
      }

      return fail({
        failure: {
          kind: FailureKind.StepPlayFailure,
          stepKind: 'Locate',
        },
        failureReason: `${error.message}`,
        error,
      });
    }
  }
}
